/**
 * GA4 view_item — single source: JS only.
 * Pushes once on PDP load, then on variant:update when SKU changes.
 * Uses GA4 standard event name per spec.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '[id^="product-information-"]';
  var VARIANT_UPDATE_EVENT = 'variant:update';

  var section = document.querySelector(SECTION_SELECTOR);
  if (!section) return;
  /** @type {Window & { __datalayerViewItemDone?: boolean }} */
  var winCheck = window;
  if (winCheck.__datalayerViewItemDone) return;
  winCheck.__datalayerViewItemDone = true;

  var lastFiredSku = section.getAttribute('data-datalayer-initial-sku');
  if (lastFiredSku != null) lastFiredSku = String(lastFiredSku).trim() || null;
  var initialPushDone = false;

  /**
   * Only include value in obj[key] if value is truthy and (for number) not 0.
   * @param {Record<string, string|number>} obj
   * @param {string} key
   * @param {string|number|undefined|null} value
   */
  function setIfValid(obj, key, value) {
    if (value == null) return;
    if (typeof value === 'number' && value <= 0) return;
    if (typeof value === 'string' && value.trim() === '') return;
    obj[key] = value;
  }

  /**
   * Apply category hierarchy from data-datalayer-category-levels to item.
   * Format: "Level1|Level2|Level3" → item_category, item_category2, item_category3.
   * When absent, use product_type for item_category.
   * @param {Record<string, *>} item
   */
  function applyCategoryLevels(item) {
    if (!section) return;
    var levelsStr = (section.getAttribute('data-datalayer-category-levels') || '').trim();
    var productType = (section.getAttribute('data-datalayer-product-type') || '').trim();
    if (levelsStr) {
      var levels = levelsStr.split('|').map(function (s) { return s.trim(); }).filter(Boolean);
      for (var i = 0; i < levels.length; i++) {
        var key = i === 0 ? 'item_category' : 'item_category' + (i + 1);
        setIfValid(item, key, levels[i]);
      }
    }
    if (!item.item_category) {
      if (productType) setIfValid(item, 'item_category', productType);
      else setIfValid(item, 'item_category', (section.getAttribute('data-datalayer-item-list-name') || '').trim());
    }
  }

  /**
   * Build item from section data attributes (initial load).
   * Includes image_url, url, variant_id for Klaviyo mapping.
   */
  function getInitialItem() {
    if (!section) return null;
    var itemId = section.getAttribute('data-datalayer-initial-sku');
    if (!itemId || String(itemId).trim() === '') return null;

    var item = {
      item_id: String(itemId).trim(),
      item_name: (section.getAttribute('data-datalayer-product-title') || '').trim() || '',
      quantity: 1
    };
    setIfValid(item, 'item_brand', (section.getAttribute('data-datalayer-product-vendor') || '').trim());
    applyCategoryLevels(item);
    setIfValid(item, 'item_list_name', (section.getAttribute('data-datalayer-item-list-name') || '').trim());
    setIfValid(item, 'image_url', (section.getAttribute('data-datalayer-image-url') || '').trim());
    setIfValid(item, 'url', (section.getAttribute('data-datalayer-product-url') || '').trim());
    var variantIdStr = (section.getAttribute('data-datalayer-variant-id') || '').trim();
    if (variantIdStr) {
      var variantIdNum = parseInt(variantIdStr, 10);
      if (!isNaN(variantIdNum)) setIfValid(item, 'variant_id', variantIdNum);
    }
    var priceStr = section.getAttribute('data-datalayer-initial-price');
    if (priceStr != null && priceStr !== '') {
      var priceNum = parseFloat(priceStr);
      if (!isNaN(priceNum) && priceNum > 0) setIfValid(item, 'price', priceNum);
    }
    return item;
  }

  /**
   * Build item from variant:update event. Includes image_url, url, variant_id for Klaviyo.
   * @param {{ sku?: string, id: number, price?: number, featured_media?: { preview_image?: { src?: string } } } | null} resource
   */
  function getItemFromVariant(resource) {
    if (!resource || !section) return null;
    var itemId = (resource.sku && String(resource.sku).trim()) ? String(resource.sku).trim() : String(resource.id);
    var item = {
      item_id: itemId,
      item_name: (section.getAttribute('data-datalayer-product-title') || '').trim() || '',
      quantity: 1
    };
    setIfValid(item, 'item_brand', (section.getAttribute('data-datalayer-product-vendor') || '').trim());
    applyCategoryLevels(item);
    setIfValid(item, 'item_list_name', (section.getAttribute('data-datalayer-item-list-name') || '').trim());
    setIfValid(item, 'url', (section.getAttribute('data-datalayer-product-url') || '').trim());
    if (typeof resource.id === 'number') setIfValid(item, 'variant_id', resource.id);
    var imgSrc = resource.featured_media && resource.featured_media.preview_image && resource.featured_media.preview_image.src;
    setIfValid(item, 'image_url', (imgSrc || section.getAttribute('data-datalayer-image-url') || '').trim());
    if (typeof resource.price === 'number' && resource.price > 0) setIfValid(item, 'price', resource.price * 0.01);
    return item;
  }

  /**
   * @param {{ item_id: string, item_name: string, quantity: number, item_category?: string, price?: number }} item
   * @param {string} [currencyAttr]
   */
  function pushViewItem(item, currencyAttr) {
    if (!item || !section) return;
    /** @type {Window & { dataLayer?: object[] }} */
    var win = window;
    if (!win.dataLayer) win.dataLayer = [];
    var currency = (currencyAttr || section.getAttribute('data-datalayer-currency') || '').trim();
    /** @type {{ items: object[], currency?: string }} */
    var ecommerce = { items: [item] };
    if (currency.length > 0 && currency.length <= 10) ecommerce.currency = currency;
    win.dataLayer.push({ ecommerce: null });
    win.dataLayer.push({ event: 'view_item', ecommerce: ecommerce });
  }

  function doInitialPush() {
    if (initialPushDone || !section) return;
    var item = getInitialItem();
    if (!item) return;
    initialPushDone = true;
    pushViewItem(item);
  }

  /**
   * @param {Event} event
   */
  function onVariantUpdate(event) {
    var target = event.target;
    if (target instanceof Element && target.closest('.quick-add-modal')) return;
    var detail = event instanceof CustomEvent ? event.detail : null;
    var resource = detail && detail.resource;
    var item = getItemFromVariant(resource);
    if (!item) return;
    var sku = item.item_id;
    if (lastFiredSku === sku) return;
    lastFiredSku = sku;
    pushViewItem(item);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', doInitialPush);
  } else {
    doInitialPush();
  }
  document.addEventListener(VARIANT_UPDATE_EVENT, onVariantUpdate);
})();
